const HIDE_AFTER_MS = 5000;
const GAP_BELOW_HEADER = 8;

/**
 * Bars pinned to the top of the viewport. The brand header swaps between
 * relative and fixed on scroll and is shorter once fixed, and the announcement
 * bar scrolls away entirely, so the offset has to be measured rather than
 * hardcoded. The theme's own --header-height is 0px because it measures the
 * hidden Prestige header, so it cannot be used here.
 */
const TOP_BAR_SELECTORS = ['.AnnouncementBar', '.floated-header-wrapper', '.Header'];

/**
 * Shows cart failures raised by CartApi. Knows nothing about the cart itself:
 * it only listens for `cart:error` and renders the message it carries.
 */
class CartNotification extends HTMLElement {
  #message = null;
  #closeButton = null;
  #timer = null;
  #frame = null;

  connectedCallback() {
    this.#message = this.querySelector('.cart-notification__message');
    this.#closeButton = this.querySelector('.cart-notification__close');

    document.addEventListener('cart:error', this.#handleError);
    this.#closeButton?.addEventListener('click', this.hide);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:error', this.#handleError);
    this.#closeButton?.removeEventListener('click', this.hide);
    this.#stopTrackingHeader();
    clearTimeout(this.#timer);
  }

  /** Sits the message just under whatever is currently pinned to the top. */
  #reposition = () => {
    let lowestEdge = 0;

    for (const selector of TOP_BAR_SELECTORS) {
      for (const bar of document.querySelectorAll(selector)) {
        const rect = bar.getBoundingClientRect();
        if (rect.height === 0 || getComputedStyle(bar).visibility === 'hidden') continue;

        lowestEdge = Math.max(lowestEdge, rect.bottom);
      }
    }

    if (lowestEdge <= 0) {
      // Nothing pinned at the top: fall back to the value in the stylesheet.
      this.style.removeProperty('--cart-notification-inset-block-start');
      return;
    }

    this.style.setProperty(
      '--cart-notification-inset-block-start',
      `${Math.round(lowestEdge + GAP_BELOW_HEADER)}px`
    );
  };

  #onViewportChange = () => {
    if (this.#frame) return;

    this.#frame = requestAnimationFrame(() => {
      this.#frame = null;
      this.#reposition();
    });
  };

  #startTrackingHeader() {
    this.#reposition();
    window.addEventListener('scroll', this.#onViewportChange, { passive: true });
    window.addEventListener('resize', this.#onViewportChange);
  }

  #stopTrackingHeader() {
    window.removeEventListener('scroll', this.#onViewportChange);
    window.removeEventListener('resize', this.#onViewportChange);

    if (this.#frame) {
      cancelAnimationFrame(this.#frame);
      this.#frame = null;
    }
  }

  /** @param {CustomEvent<{ message?: string }>} event */
  #handleError = (event) => {
    const message = event.detail?.message;
    if (!message) return;

    this.show(message);
  };

  /** @param {string} message */
  show(message) {
    if (!this.#message) return;

    this.#message.textContent = message;
    this.#startTrackingHeader();
    this.classList.add('cart-notification--visible');
    // The host stays in the DOM so the live region can announce, but the close
    // button must not sit in the tab order while nothing is on screen.
    if (this.#closeButton) this.#closeButton.hidden = false;

    clearTimeout(this.#timer);
    this.#timer = setTimeout(this.hide, HIDE_AFTER_MS);
  }

  hide = () => {
    this.classList.remove('cart-notification--visible');
    if (this.#closeButton) this.#closeButton.hidden = true;
    this.#stopTrackingHeader();
    clearTimeout(this.#timer);
  };
}

customElements.define('cart-notification', CartNotification);
